<template>
  <UIDialog :is-open :title :z-index size="md" flat @close="handleClose">
    <div v-circular-tab class="precision-form">
      <div class="precision-form__items">
        <div class="precision-form__item-title">{{ accountTitle }}</div>
        <div class="precision-form__item-title">{{ amountTitle }}</div>
        <PrecisionFormItem
          v-for="(owner, index) in owners"
          :key="index"
          :owner
          :index
          :listValues
          @update="handleUpdateOwner"
          @delete="handleDeleteOwner"
        />
        <div class="precision-form__button">
          <UIButton
            label="Owner"
            variant="secondary"
            size="sm"
            :leading="PlusIcon"
            @click="handleAddOwner"
          />
        </div>
        <div class="precision-form__item-total">Total</div>
        <div class="precision-form__item-total" :class="totalClasses">{{ amountFormatter(totalAmount) }}</div>
      </div>
      <UIButtonsGroup>
        <template #default="{ short }">
          <UIButtonClose :short @click="handleClose" />
          <UIButtonSave
            :short
            label="Update"
            type="submit"
            @click="handleComplete"
          />
        </template>
      </UIButtonsGroup>
    </div>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { isEqual } from 'lodash'

import { DropdownListData, ModalClass, TransactionSettings } from '@types'

import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'

import { displayAmount } from '@/entities/transactions/utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { UIDialog } from '@ui/modals'
import { UIButton, UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { PlusIcon } from '@heroicons/vue/24/outline'
import PrecisionFormItem from './PrecisionFormItem.vue'

type Props = {
  modal: ModalClass<any>
  entrySettings: TransactionSettings['entries'][0]
  assetId: string
  amounts: [string[], number][]
  paths: string[][]
}

type Emits = {
  'complete': [data: [string[], number][]]
  'cancel': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const assetsBunchStore = useAssetsBunchStore()

const owners = ref<[string[], number, boolean][]>(props.amounts.map(amount => [amount[0], amount[1], false]))

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const assetName = computed(() => assetsBunchStore.getElementById(props.assetId)?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE)
const title = computed(() => `Asset ${assetName.value} precision amount${props.amounts.length > 1 ? 's' : ''}`)

const accountTitle = computed(() => props.entrySettings.account?.title)
const amountTitle = computed(() => props.entrySettings.amount?.title)

const listValues = computed(() => props.paths.reduce((acc, path) => {
  if (props.amounts.some(amount => !isEqual(amount[0], path))) {
    const label = path.slice(1).map(item => assetsBunchStore.getElementById(item)?.field<string>(ASSET_FIELD.NAME).value).join(' -> ')
    acc.push({
      value: JSON.stringify([...path, props.assetId]),
      label,
    })
  }
  return acc
}, [] as DropdownListData[]))

const totalAmount =  computed(() => {
  return owners.value.reduce((acc, value) => {
    return acc + value[1]
  }, 0)
})

const totalClasses = computed(() => {
  return totalAmount.value <= 1 ? 'precision-form__item-total--positive' : 'precision-form__item-total--negative'
})

const amountFormatter = (value: number | null | undefined) => {
  const settings = props.entrySettings.amount
  if (!settings) return ''
  const isPercent = settings?.rule === 'percent'
  return displayAmount(value, isPercent)
}

const handleClose = () => {
  emit('cancel')
  props.modal.close()
}

const handleComplete = () => {
  emit('complete', owners.value.map(owner => owner.slice(0, 2) as [string[], number]))
  props.modal.close()
}

const handleAddOwner = () => {
  owners.value.push([[], 0, true])
}

const handleUpdateOwner = (index: number, value: [string[], number, boolean]) => {
  owners.value[index] = value
}

const handleDeleteOwner = (index: number) => {
  owners.value.splice(index, 1)
}
</script>

<style scoped>
.precision-form {
  @apply pt-4 sm:pt-0;

  &__items {
    @apply grid grid-cols-[1fr_min-content] gap-y-2;

    :deep(> div) {
      @apply px-3 odd:pl-default even:pr-default;
      @apply whitespace-nowrap;
    }
  }

  &__item-title,
  &__item-total {
    @apply py-2;
    @apply bg-body-gray;
    @apply text-light-text;
    @apply text-sm font-medium;
  }

  &__item-title {
    @apply hidden sm:block;
  }

  &__item-total {
    &--positive {
      @apply text-gray-950 dark:text-green-400;
    }
    &--negative {
      @apply text-red-600 dark:text-red-400;
    }
  }

  &__button {
    @apply col-span-2;
    @apply !px-default;

    .ui-button {
      @apply w-full;
    }
  }

  &__button + &__item-total {
    @apply !pl-default !pr-3;
  }

  &__item-total {
    @apply last:pr-default !pl-3;
  }

  .ui-buttons-group {
    @apply py-4 px-default;
  }
}
</style>
